import React from 'react';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { ExternalCalendarEvent } from 'shared/types/external-calendar-event';
import { FilterDocument } from 'shared/types/filter-document';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { LinkedItemSection } from 'shared/types/linked-item-section';
import { RelationshipUpdate } from 'shared/types/relationship';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';
import { DueDisplayOptions } from 'shared/types/task-base';
import { WeekDays } from 'shared/types/week-days';

export const noGroupingId = 'noGrouping';

export type GroupSetting = {
  showEmptyGroups?: boolean;
  hideNoGoalGroup?: boolean;
  showEmptyDate?: boolean;
  showEmptyOverdue?: boolean;
  showEmptyNoDate?: boolean;
  showSingleDeadlineAsNoGrouping?: boolean;
  sections?: LinkedItemSection[] | null | undefined;
  onUpdateSection?: (id: ID, name: string) => void;
  goalId?: ID;
  lifeAreaId?: ID;
  tagId?: ID;
  hideNewTaskRow?: boolean;
  displayDue?: DueDisplayOptions[];
  showRepeatingAsTodo?: boolean;
  /**
   * For CompletedDate grouping: only include completion occurrences whose
   * date is on or after this timestamp. Used to bound the otherwise
   * unbounded iteration over a habit's full schedule history.
   */
  completedFromDate?: Date;
  /**
   * External calendar events to display within date sections.
   * Currently only used by the UpcomingDeadline grouping.
   */
  externalEvents?: ExternalCalendarEvent[];
  timeFormat?: TimeFormatOptions;
};

export type GroupSettings = Partial<Record<GroupByMode, GroupSetting>>;

export type TaskFilterListProps = {
  filterDocument: FilterDocument;
  defaultFilterDocument?: FilterDocument;
  onReorder?: (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTasks: Task[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => void;
  customOrder: Record<ID, ID[]>;
  onSectionCollapse?: (sectionIds: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  includeDuration?: boolean;
  onReset?: () => void;
  denseFormat?: boolean;
  includeTaskWithAction?: boolean;
  groupSettings?: GroupSettings;
  groupSettingOverrides?: GroupSetting;
  excludeHiddenTodoHabits?: boolean;
  defaultNewTaskDate?: Date;
  includeArchivedGoals?: boolean;
  includeArchived?: boolean;
  goals?: Goal[];
  canReorderGoals?: boolean;
  EmptyState?: React.ComponentType;
};

export type GroupedListProps = {
  tasks: Task[];
  lifeAreas: LifeArea[];
  goals: Goal[];
  tags: Tag[];
  onReorder?: (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTask: Task[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => void;
  sortingMode: SortingMode | undefined | null;
  collapsedSections?: ID[];
  onSectionCollapse?: (sectionIds: ID[]) => void;
  customOrder: Record<ID, ID[]>; // `noGrouping: []` for noGrouping
  weekStartsOn: WeekDays;
  scrollElement?: React.RefObject<HTMLDivElement>;
  denseFormat?: boolean;
  groupSettings: GroupSetting;
  filterDocument?: FilterDocument;
  isContextMenuOpen?: boolean;
  defaultNewTaskDate?: Date;
  canReorderGoals?: boolean;
};
